// Kargo şirketi takip URL'lerini yöneten utility fonksiyonları

/**
 * Kargonomi API'den desteklenen kargo şirketlerinin takip URL'leri
 * Gerçek çalışan URL'ler ile güncellenmiş
 */
const CARGO_TRACKING_URLS = {
    // Türkiye ana kargo şirketleri - Gerçek çalışan URL'ler
    'aras': 'https://kargotakip.araskargo.com.tr/?code=',
    'aras_kargo': 'https://kargotakip.araskargo.com.tr/?code=',
    
    'yurtici': 'https://www.yurticikargo.com/tr/online-islemler/gonderi-sorgula?code=',
    'yurtici_kargo': 'https://www.yurticikargo.com/tr/online-islemler/gonderi-sorgula?code=',
    
    'mng': 'https://www.mngkargo.com.tr/kargo-takip?code=',
    'mng_kargo': 'https://www.mngkargo.com.tr/kargo-takip?code=',
    
    'ptt': 'https://gonderitakip.ptt.gov.tr/Track/Verify?q=',
    'ptt_kargo': 'https://gonderitakip.ptt.gov.tr/Track/Verify?q=',
    
    'surat': 'https://www.suratkargo.com.tr/',
    'surat_kargo': 'https://www.suratkargo.com.tr/',
    'sürat': 'https://www.suratkargo.com.tr/',
    'sürat_kargo': 'https://www.suratkargo.com.tr/',
    
    'hepsijet': 'https://www.hepsijet.com/',
    
    'kolaygelsin': 'https://kolaygelsin.com/track?trackingCode=',
    'kolay_gelsin': 'https://kolaygelsin.com/track?trackingCode=',
    
    // Uluslararası kargo şirketleri
    'ups': 'https://www.ups.com/track?loc=tr_TR&tracknum=',
    'ups_kargo': 'https://www.ups.com/track?loc=tr_TR&tracknum=',
    
    'dhl': 'https://www.dhl.com.tr/tr/express/tracking.html?AWB=',
    'dhl_kargo': 'https://www.dhl.com.tr/tr/express/tracking.html?AWB=',
    
    'fedex': 'https://www.fedex.com/fedextrack/?trknbr=',
    'fedex_kargo': 'https://www.fedex.com/fedextrack/?trknbr='
};

/**
 * Kargo şirketi adını normalize eder (küçük harf, boşlukları _ ile değiştirir)
 * @param {string} companyName - Kargo şirketi adı
 * @returns {string} Normalize edilmiş şirket adı
 */
export const normalizeCompanyName = (companyName) => {
    if (!companyName) return '';
    
    // Özel karakterleri temizle ve normalize et
    let normalized = companyName
        .toLowerCase()
        .trim()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    
    // Özel durumlar için mapping
    const specialMappings = {
        'ups_kargo': 'ups',
        'surat_kargo': 'surat',
        'sürat_kargo': 'surat',
        'aras_kargo': 'aras',
        'yurtici_kargo': 'yurtici',
        'mng_kargo': 'mng',
        'ptt_kargo': 'ptt',
        'dhl_kargo': 'dhl',
        'fedex_kargo': 'fedex'
    };
    
    return specialMappings[normalized] || normalized;
};

/**
 * Kargo şirketi için takip URL'si oluşturur
 * @param {string} companyName - Kargo şirketi adı
 * @param {string} trackingNumber - Takip numarası
 * @returns {string} Takip URL'si veya null
 */
export const generateTrackingUrl = (companyName, trackingNumber) => {
    if (!trackingNumber) return null;
    
    const normalizedCompany = normalizeCompanyName(companyName);
    const baseUrl = CARGO_TRACKING_URLS[normalizedCompany];
    
    // Sadece desteklenen kargo şirketleri için URL döndür
    if (!baseUrl) {
        console.warn('Desteklenmeyen kargo şirketi:', companyName);
        return null;
    }
    
    // HepsiJet ve Sürat Kargo için sadece ana sayfa (takip numarası eklenmez)
    const homepageOnlyCompanies = ['hepsijet', 'surat', 'sürat'];
    if (homepageOnlyCompanies.includes(normalizedCompany)) {
        return baseUrl; // Sadece ana sayfa URL'si döndür
    }
    
    // Diğer kargo şirketleri için takip numarası ekle
    return baseUrl + encodeURIComponent(trackingNumber);
};

/**
 * Kargo şirketinin desteklenip desteklenmediğini kontrol eder
 * @param {string} companyName - Kargo şirketi adı
 * @returns {boolean} Destekleniyorsa true
 */
export const isCompanySupported = (companyName) => {
    const normalizedCompany = normalizeCompanyName(companyName);
    return CARGO_TRACKING_URLS.hasOwnProperty(normalizedCompany);
};

/**
 * Takip numarasının geçerli olup olmadığını kontrol eder
 * @param {string} trackingNumber - Takip numarası
 * @returns {boolean} Geçerliyse true
 */
export const isValidTrackingNumber = (trackingNumber) => {
    if (!trackingNumber) return false;
    
    // Temel validasyon: en az 3 karakter, alfanumerik
    const cleanNumber = trackingNumber.trim();
    return cleanNumber.length >= 3 && /^[a-zA-Z0-9\-_]+$/.test(cleanNumber);
};

/**
 * Kargo şirketi adını güzelleştirir (görüntüleme için)
 * @param {string} companyName - Kargo şirketi adı
 * @returns {string} Güzelleştirilmiş şirket adı
 */
export const beautifyCompanyName = (companyName) => {
    if (!companyName) return 'Bilinmeyen Kargo';
    
    // Kargonomi'den desteklenen kargo şirketleri için mapping
    const specialNames = {
        'aras': 'Aras Kargo',
        'aras_kargo': 'Aras Kargo',
        'yurtici': 'Yurtiçi Kargo',
        'yurtici_kargo': 'Yurtiçi Kargo',
        'mng': 'MNG Kargo',
        'mng_kargo': 'MNG Kargo',
        'ptt': 'PTT Kargo',
        'ptt_kargo': 'PTT Kargo',
        'surat': 'Sürat Kargo',
        'surat_kargo': 'Sürat Kargo',
        'sürat': 'Sürat Kargo',
        'sürat_kargo': 'Sürat Kargo',
        'hepsijet': 'HepsiJet',
        'kolaygelsin': 'Kolay Gelsin',
        'kolay_gelsin': 'Kolay Gelsin',
        'ups': 'UPS',
        'ups_kargo': 'UPS',
        'dhl': 'DHL',
        'dhl_kargo': 'DHL',
        'fedex': 'FedEx',
        'fedex_kargo': 'FedEx'
    };
    
    const normalized = normalizeCompanyName(companyName);
    return specialNames[normalized] || companyName;
};

/**
 * Takip linkini yeni sekmede açar
 * @param {string} companyName - Kargo şirketi adı
 * @param {string} trackingNumber - Takip numarası
 * @returns {boolean} Başarılı ise true
 */
export const openTrackingLink = (companyName, trackingNumber) => {
    if (!isValidTrackingNumber(trackingNumber)) {
        console.warn('Geçersiz takip numarası:', trackingNumber);
        return false;
    }
    
    const url = generateTrackingUrl(companyName, trackingNumber);
    if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return true;
    } else {
        console.warn('Desteklenmeyen kargo şirketi:', companyName);
        return false;
    }
};

/**
 * Desteklenen kargo şirketlerinin listesini döner
 * @returns {Array} Desteklenen kargo şirketleri
 */
export const getSupportedCompanies = () => {
    return Object.keys(CARGO_TRACKING_URLS)
        .map(key => ({
            key,
            name: beautifyCompanyName(key),
            supported: true
        }));
};

export default {
    normalizeCompanyName,
    generateTrackingUrl,
    isCompanySupported,
    isValidTrackingNumber,
    beautifyCompanyName,
    openTrackingLink,
    getSupportedCompanies
};